import React, { useEffect, useRef, useState } from "react";
import { ShieldCheck, X, RotateCcw } from "lucide-react";
import { useAuth } from "@/components/AuthContext";
import { TURNSTILE_SITE_KEY, HUMAN_CHECK_EVENT } from "@/lib/humanCheck";

// Cloudflare Turnstile challenge (2026-10-01). Opened by getHumanToken()
// (lib/humanCheck.js); usually passes automatically in a second or two
// without a puzzle. Mounted once in App.js.
//
// Safety net (2026-10-01): the check is never cut off while it runs. After
// SLOW_AFTER_MS, "Restart the check" and "Continue with Google" appear
// under it; if Cloudflare reports an error (or its script is blocked) the
// widget is replaced by "Try again" / "Continue with Google" -- Google
// sign-in isn't challenged and signed-in users never are, so a real person
// always has a way in while anonymous bots still can't reach the AI.
const SLOW_AFTER_MS = 12000;
const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

function loadTurnstile() {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  return new Promise((resolve, reject) => {
    let s = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
    if (!s) {
      s = document.createElement("script");
      s.src = SCRIPT_SRC;
      s.async = true;
      document.head.appendChild(s);
    }
    s.addEventListener("load", () => resolve(window.turnstile));
    s.addEventListener("error", () => reject(new Error("Couldn't load the human check.")));
  });
}

export function HumanCheckModal({ isLight }) {
  const [request, setRequest] = useState(null); // { resolve, reject }
  const [failed, setFailed] = useState(false); // Cloudflare reported an error / script blocked
  const [slow, setSlow] = useState(false); // still checking after SLOW_AFTER_MS
  const [attempt, setAttempt] = useState(0);
  const box = useRef(null);
  const widgetId = useRef(null);
  const { login } = useAuth() || {};

  useEffect(() => {
    const onRequest = (e) => { setFailed(false); setSlow(false); setRequest(e.detail); };
    window.addEventListener(HUMAN_CHECK_EVENT, onRequest);
    return () => window.removeEventListener(HUMAN_CHECK_EVENT, onRequest);
  }, []);

  useEffect(() => {
    if (!request || !TURNSTILE_SITE_KEY || failed) return undefined;
    let cancelled = false;
    setSlow(false);
    // Soft timer: the check keeps running; we only ADD the way-out options
    // underneath it. Cloudflare's automatic check can take a while on a slow
    // connection and gives no "still working" signal, so never cut it off.
    const timer = setTimeout(() => { if (!cancelled) setSlow(true); }, SLOW_AFTER_MS);
    loadTurnstile().then((ts) => {
      if (cancelled || !box.current) return;
      widgetId.current = ts.render(box.current, {
        sitekey: TURNSTILE_SITE_KEY,
        theme: isLight ? "light" : "dark",
        callback: (token) => { request.resolve(token); setRequest(null); },
        "error-callback": () => { setFailed(true); return true; },
        "unsupported-callback": () => setFailed(true),
        // The checkbox / token expired before it was used: start a fresh check.
        "timeout-callback": () => { try { ts.reset(widgetId.current); } catch {} },
        "expired-callback": () => { try { ts.reset(widgetId.current); } catch {} },
      });
    }).catch(() => setFailed(true));
    return () => {
      cancelled = true;
      clearTimeout(timer);
      try { if (widgetId.current != null) window.turnstile?.remove(widgetId.current); } catch {}
      widgetId.current = null;
    };
  }, [request, isLight, failed, attempt]);

  if (!request) return null;
  const cancel = () => { request.reject(new Error("Human check cancelled")); setRequest(null); };
  const retry = () => { setFailed(false); setSlow(false); setAttempt((n) => n + 1); };
  const google = () => {
    request.reject(new Error("Continuing with Google sign-in"));
    setRequest(null);
    if (login) login(false);
  };
  const muted = isLight ? "text-slate-500" : "text-[#A9C2B6]";
  const options = (
    <div className="mt-4 space-y-2" data-testid={failed ? "human-check-fallback" : "human-check-slow"}>
      <p className={`text-sm ${isLight ? "text-slate-600" : "text-[#E3EFE9]"}`}>
        {failed ? "We couldn't load the check on your connection." : "Taking longer than usual?"}
      </p>
      <button type="button" data-testid="human-check-retry" onClick={retry} className={`w-full inline-flex items-center justify-center gap-2 rounded-full border py-2.5 text-sm font-bold ${isLight ? "border-slate-200 text-slate-700 hover:border-[#A67C52]" : "border-white/15 text-white hover:border-[#A67C52]"}`}>
        <RotateCcw className="size-4" /> {failed ? "Try again" : "Restart the check"}
      </button>
      <button type="button" data-testid="human-check-google" onClick={google} className="w-full rounded-full bg-white py-2.5 text-sm font-bold text-slate-900 shadow hover:bg-slate-100">
        Continue with Google
      </button>
      <p className={`text-[11px] ${isLight ? "text-slate-400" : "text-[#9FB8AC]"}`}>Signing in skips this check — every account gets free AI tries.</p>
    </div>
  );
  return (
    <div className={`fixed inset-0 z-[90] flex items-end sm:items-center justify-center backdrop-blur-md ${isLight ? "bg-[#FBF6EF]/60" : "bg-[#07140F]/60"}`} data-testid="human-check-modal">
      <div className={`relative w-full sm:max-w-sm rounded-t-3xl sm:rounded-3xl border p-6 text-center shadow-2xl ${isLight ? "bg-white border-slate-200" : "bg-[#0F211B] border-white/10"}`}>
        <button type="button" onClick={cancel} aria-label="Close" className={`absolute right-4 top-4 ${isLight ? "text-slate-400" : "text-neutral-500 hover:text-white"}`}><X className="size-4" /></button>
        <span className="mx-auto grid size-12 place-items-center rounded-2xl bg-gradient-to-br from-[#C9A27A] to-[#8A6640] text-white"><ShieldCheck className="size-6" /></span>
        <h2 className={`mt-3 text-lg font-black ${isLight ? "text-slate-900" : "text-white"}`}>Quick check you're human</h2>
        <p className={`mt-1 text-sm ${muted}`}>This keeps bots from overloading our AI. It usually takes a few seconds.</p>
        {!failed && <div ref={box} key={attempt} className="mt-4 flex justify-center min-h-[65px]" data-testid="human-check-widget" />}
        {(failed || slow) && options}
      </div>
    </div>
  );
}
